import React, { useContext, useMemo } from 'react';

import {
  type QueryParams as IdeaActivityQueryParams,
  loadQuery as loadIdeaActivity,
  loadMore as loadMoreIdeaActivity,
  removeIdeaComments,
  upsertIdeaComments,
} from 'common/actions/ideaActivityQueries';
import {
  type QueryParams,
  loadIdea,
  recomputeAllIdeaScores,
  reloadIdea,
} from 'common/actions/ideas';
import connect from 'common/core/connect';

import type { IdeaActivity, IdeaComment } from 'common/api/endpoints/ideaActivity';
import type { QueryState } from 'common/reducers/queryReducerFactory';

type IdeasContextValue = {
  loadIdea: (queryParams: QueryParams) => Promise<void>;
  loadIdeaActivity: (queryParams: IdeaActivityQueryParams) => Promise<void>;
  loadMoreIdeaActivity: (query: QueryState<IdeaActivity, IdeaActivityQueryParams>) => Promise<void>;
  reloadIdea: ({ ideaID }: { ideaID: string }) => Promise<void>;
  recomputeAllIdeaScores: () => Promise<void>;
  removeIdeaComments: (queryParams: IdeaActivityQueryParams, commentIDs: string[]) => void;
  upsertIdeaComments: (queryParams: IdeaActivityQueryParams, comments: IdeaComment[]) => void;
};

export const IdeasContext = React.createContext<IdeasContextValue>({
  loadIdea: (_queryParams: QueryParams) => Promise.resolve(),
  loadIdeaActivity: (_queryParams: IdeaActivityQueryParams) => Promise.resolve(),
  loadMoreIdeaActivity: (_query: QueryState<IdeaActivity, IdeaActivityQueryParams>) =>
    Promise.resolve(),
  reloadIdea: ({ ideaID: _ideaID }: { ideaID: string }) => Promise.resolve(),
  recomputeAllIdeaScores: () => Promise.resolve(),
  removeIdeaComments: (_queryParams: IdeaActivityQueryParams, _commentIDs: string[]) => {},
  upsertIdeaComments: (_queryParams: IdeaActivityQueryParams, _comments: IdeaComment[]) => {},
});

export const useIdeasContext = () => {
  return useContext(IdeasContext);
};

type ConnectProps = {
  loadIdea: (queryParams: QueryParams) => Promise<void>;
  loadIdeaActivity: (queryParams: IdeaActivityQueryParams) => Promise<void>;
  loadMoreIdeaActivity: (query: QueryState<IdeaActivity, IdeaActivityQueryParams>) => Promise<void>;
  reloadIdea: ({ ideaID }: { ideaID: string }) => Promise<void>;
  recomputeAllIdeaScores: () => Promise<void>;
  removeIdeaComments: (queryParams: IdeaActivityQueryParams, commentIDs: string[]) => void;
  upsertIdeaComments: (queryParams: IdeaActivityQueryParams, comments: IdeaComment[]) => void;
};
type OwnProps = {
  children: React.ReactNode;
};
type Props = ConnectProps & OwnProps;
function IdeasContainer({
  children,
  loadIdea,
  loadIdeaActivity,
  loadMoreIdeaActivity,
  reloadIdea,
  recomputeAllIdeaScores,
  removeIdeaComments,
  upsertIdeaComments,
}: Props) {
  const contextValue = useMemo(
    () => ({
      loadIdea,
      loadIdeaActivity,
      loadMoreIdeaActivity,
      reloadIdea,
      recomputeAllIdeaScores,
      removeIdeaComments,
      upsertIdeaComments,
    }),
    [
      loadIdea,
      loadIdeaActivity,
      loadMoreIdeaActivity,
      reloadIdea,
      recomputeAllIdeaScores,
      removeIdeaComments,
      upsertIdeaComments,
    ],
  );
  return <IdeasContext.Provider value={contextValue}>{children}</IdeasContext.Provider>;
}

export default connect(null, {
  loadIdea,
  loadIdeaActivity,
  loadMoreIdeaActivity,
  reloadIdea,
  recomputeAllIdeaScores,
  removeIdeaComments,
  upsertIdeaComments,
})(IdeasContainer) as unknown as React.FC<OwnProps>;
